/* 
  Service file that serve all api calling
*/

import axios from 'axios';
import StorageService from 'services/StorageService'
import { API_TIMEOUT } from 'constants/General';
import { BASE_URL } from 'config/environment';

/*
  Silent access-token refresh (F-7)

  Access tokens now live 15 minutes. When a request gets 401, we use the stored
  refresh token once to get a new pair and replay the request, so the user never
  notices. All requests that fail at the same moment share a single refresh call:
  refresh tokens rotate and the old one is blacklisted, so two parallel refreshes
  with the same token would log the user out.
  If the refresh fails (expired, blacklisted, missing), the original 401 is
  passed on unchanged and the existing handling (clear session -> login) applies.
*/
const NO_REFRESH_PATHS = ['/accounts/login/', '/accounts/refresh-token/', '/accounts/logout/'];
const refreshClient = axios.create({ timeout: API_TIMEOUT });
let refreshInFlight = null;

const refreshAccessToken = () => {
  if (!refreshInFlight) {
    const refresh = StorageService.instance.getRefreshToken();
    refreshInFlight = (refresh && refresh !== 'null'
      ? refreshClient.post(`${BASE_URL}/accounts/refresh-token/`, { refresh }).then((res) => {
          const { access, refresh: rotated } = res?.data || {};
          if (!access) throw new Error('No access token in refresh response');
          StorageService.instance.setAccessToken(access);
          if (rotated) StorageService.instance.setRefreshToken(rotated);
          return access;
        })
      : Promise.reject(new Error('No refresh token'))
    ).finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const url = original?.url || '';
    const skip = NO_REFRESH_PATHS.some((p) => url.includes(p));
    if (error?.response?.status !== 401 || !original || original._retried || skip) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      const access = await refreshAccessToken();
      if (original.headers && typeof original.headers.set === 'function') {
        original.headers.set('Authorization', `Bearer ${access}`);
      } else {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${access}` };
      }
      return axios(original);
    } catch (refreshError) {
      return Promise.reject(error); // keep the original 401 for the existing handler
    }
  }
);

export const apiClient = (formData) => {
  const token = StorageService.instance.getAccessToken();

  // Dummy Condition, will remove with actual api
  let defaultOptions = !! formData ? { headers: {
    Authorization: token ? `Bearer ${token || null}` : '',
    'Content-Type': 'multipart/form-data',
    'ngrok-skip-browser-warning': '1'
  }}  :  !!token? {
  headers: {
    Authorization: token ? `Bearer ${token || null}` : '',
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': '1'
  }
}: {
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': '1'
  }
};
  
  // Request timeout
  defaultOptions = {
    ...defaultOptions,
    timeout: API_TIMEOUT
  }

  return {
    get: (url, options = {}) => (
      axios.get(
        `${BASE_URL}${url}`, 
        { ...defaultOptions, ...options }
      )
    ),
    post: (url, data, options = {}) => (
      axios.post(
        `${BASE_URL}${url}`, 
        data, 
        { ...defaultOptions, ...options }
      )
    ),
    put: (url, data, options = {}) => (
      axios.put(
        `${BASE_URL}${url}`, 
        data, 
        { ...defaultOptions, ...options }
      )
    ),
    patch: (url, data, options = {}) => (
      axios.patch(
        `${BASE_URL}${url}`, 
        data, 
        { ...defaultOptions, ...options }
      )
    ),
    delete: (url, data, options = {}) => (
      axios.delete(
        `${BASE_URL}${url}`, 
        { ...defaultOptions, ...options, data }
      )
    )
  };
};